import { z } from "zod";
import {
  FinishingType,
  FinishingTypes,
  PaymentTerm,
  PaymentTerms,
  Track,
  TrackDocumentType,
} from "../types";
import { PaymentTypes } from "../dtos";

// Strips invisible Unicode characters (zero-width joiners, word joiners, etc.)
// that get copied from spreadsheets and cause DB encoding errors.
const sanitizeUnitId = z
  .string()
  .optional()
  .transform((v) => v?.replace(/[^\x20-\x7E]/g, "").trim());
import { enforceMandates, MandateFields } from "@/modules/common/utils/zod";
import {
  ListingDescriptionFormFields,
  ResaleDocumentsFormFields,
} from "./listing";

export const addTrackWithCustomerSchema = () =>
  z.object({
    name: z.string().refine((value) => !!value, {
      message: "Name is required*",
    }),
    telephone: z
      .string()
      .refine((value) => !!value, {
        message: "Telephone is required*",
      })
      .refine((value) => value.length > 8, {
        message: "Invalid phone number",
      }),
    compound: z.object({
      label: z.string().min(1, "Compound is required*"),
      value: z.coerce.number().int().positive("Invalid compound ID*"),
    }),
    propertyType: z.object({
      label: z.string().min(1, "Property type is required*"),
      value: z.coerce.number().int().positive("Invalid property type ID*"),
    }),
    trackType: z.object({
      label: z.string().min(1, "Track type is required*"),
      value: z.coerce.number().int().positive("Invalid track type ID*"),
    }),
  });

export const addTrackSchema = () =>
  z.object({
    compound: z.object({
      label: z.string().min(1, "Compound is required*"),
      value: z.coerce.number().int().positive("Invalid compound ID*"),
    }),
    propertyType: z.object({
      label: z.string().min(1, "Property type is required*"),
      value: z.coerce.number().int().positive("Invalid property type ID*"),
    }),
    trackType: z.object({
      label: z.string().min(1, "Track type is required*"),
      value: z.coerce.number().int().positive("Invalid track type ID*"),
    }),
    organizationUId: z.string().optional(),
  });

export const addTrackToPropertySchema = () =>
  z.object({
    trackType: z.object({
      label: z.string().min(1, "Track Type is required*"),
      value: z.coerce.number().int().positive("Invalid Track Type ID*"),
    }),
  });

export const addTrackDetailedSchema = () =>
  z.object({
    trackType: z.object({
      label: z.string().min(1, "Track Type is required*"),
      value: z.coerce.number().int().positive("Invalid Track Type ID*"),
    }),
    property: addPropertySchema(),
    organizationUId: z.string().optional(),
  });

export const addPropertySchema = () =>
  z.object({
    compound: z
      .object({
        label: z.string().min(1, "Compound is required*"),
        value: z.coerce.number().int().positive("Invalid compound ID*"),
      })
      .optional(),
    propertyType: z
      .object({
        label: z.string().min(1, "Property type is required*"),
        value: z.coerce.number().int().positive("Invalid property type ID*"),
      })
      .optional(),
    unitId: sanitizeUnitId,
    numberOfBedrooms: z.coerce.number().int().nonnegative().optional(),
    numberOfBathrooms: z.coerce.number().int().nonnegative().optional(),
    phase: z.string().optional(),
    unitArea: z.coerce.number().int().nonnegative().optional(),
    landArea: z.coerce.number().int().nonnegative().optional(),
    gardenArea: z.coerce.number().int().nonnegative().optional(),
    roofArea: z.coerce.number().int().nonnegative().optional(),
    isUnderConstruction: z.boolean().optional(),
    floorNumber: z.coerce.number().int().nonnegative().optional(),
    numberOfFloors: z.coerce.number().int().nonnegative().optional(),
    deliveryDate: z.coerce.date().optional(),
    readyToMove: z.boolean().optional(),
    lastInstallmentDate: z.coerce.date().optional(),
    description: z
      .string()
      .nullish()
      .transform((v) => (v === "null" ? null : v)),
    finishing: z
      .object({
        label: z.enum(Object.values(FinishingTypes) as [string, ...string[]]),
        value: z.enum(Object.keys(FinishingTypes) as [FinishingType]),
      })
      .optional(),
    amenityIds: z.set(z.coerce.number().int().nonnegative()).optional(),
    propertyPrice: propertyPriceSchema.optional(),
    latitude: z.coerce.number().optional(),
    longitude: z.coerce.number().optional(),
    address: z.string().optional(),
  });

export const propertyPriceSchema = z
  .object({
    paymentType: z
      .enum(Object.values(PaymentTypes) as [PaymentTypes, ...PaymentTypes[]])
      .optional(),
    // askingPrice: z.coerce.number().int().nonnegative().optional(),
    price: z.coerce.number().int().nonnegative().optional(),
    downPayment: z.coerce.number().int().nonnegative().optional(),
    maintenance: z.coerce.number().int().nonnegative().optional(),
    remainingInstallments: z.coerce.number().int().nonnegative().optional(),
    clubhouse: z.coerce.number().int().nonnegative().optional(),
    parking: z.coerce.number().int().nonnegative().optional(),
    durationRemainingMonths: z.coerce.number().int().nonnegative().optional(),
    durationRemainingYears: z.coerce.number().int().nonnegative().optional(),
    installmentFrequency: z
      .object({
        label: z.enum(Object.values(PaymentTerms) as [string, ...string[]]),
        value: z.enum(Object.keys(PaymentTerms) as [PaymentTerm]),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    switch (data?.paymentType) {
      case PaymentTypes.CASH:
        enforceMandates(data, { price: true }, ctx, []);
        break;
      case PaymentTypes.INSTALLMENTS:
        enforceMandates(
          data,
          { remainingInstallments: true, downPayment: true },
          ctx,
          []
        );
        break;
    }
  });

export type PropertyPriceFormFields = z.infer<typeof propertyPriceSchema>;

export const editPropertySchema = z.object({
  compound: z.object({
    label: z.string().min(1, "Compound is required*"),
    value: z.coerce.number().int().positive("Invalid compound ID*"),
  }),
  propertyType: z.object({
    label: z.string().min(1, "Property type is required*"),
    value: z.coerce.number().int().positive("Invalid property type ID*"),
  }),
  unitId: sanitizeUnitId,
  numberOfBedrooms: z.coerce.number().int().nonnegative().optional(),
  numberOfBathrooms: z.coerce.number().int().nonnegative().optional(),
  phase: z.coerce.string().optional(),
  unitArea: z.coerce.number().int().nonnegative().optional(),
  landArea: z.coerce.number().int().nonnegative().optional(),
  gardenArea: z.coerce.number().int().nonnegative().optional(),
  roofArea: z.coerce.number().int().nonnegative().optional(),
  floorNumber: z.coerce.number().int().nonnegative().optional(),
  isUnderConstruction: z.boolean().optional(),
  numberOfFloors: z.coerce.number().int().nonnegative().optional(),
  deliveryDate: z.coerce.date().optional(),
  lastInstallmentDate: z.coerce.date().optional(),
  readyToMove: z.boolean().optional(),
  description: z
    .string()
    .nullish()
    .transform((v) => (v === "null" ? null : v)),
  finishing: z
    .object({
      label: z.enum(Object.values(FinishingTypes) as [string, ...string[]]),
      value: z.enum(Object.keys(FinishingTypes) as [FinishingType]),
    })
    .optional(),
  websiteListingId: z.coerce.number().int().nonnegative().optional(),
  amenityIds: z.set(z.coerce.number().int().nonnegative()).optional(),
  latitude: z.coerce.number().optional(),
  longitude: z.coerce.number().optional(),
  address: z.string().optional(),
});

export type PropertyFormFields = z.infer<typeof editPropertySchema>;

export const uploadDocumentSchema = () =>
  z.object({
    document: z.any().refine((file) => file?.length !== 0, {
      message: "Document is required",
    }),
  });

export const enum PROPERTY_TRACKS {
  FINISHING = "Unlocked finishing",
  RESALE = "Resale Seller",
  RENTAL = "Unlocked rentals",
  HOSPITALITY = "Unlocked Hospitality",
}

export const ACCEPTED_TRACK_FILE_TYPES: Record<TrackDocumentType, string[]> = {
  ["Media"]: [".jpg", ".jpeg", ".png", "image/jpeg", "image/jpg", "image/png"],
  ["Contracts"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["Proposals"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["Drawings"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["FloorPlans"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["MasterPlans"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["PowerOfAttorney"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
  ],
  ["OwnerID"]: [
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
    ".pdf",
    "application/pdf",
  ],
  ["MaterialSelection"]: [
    ".xlsx",
    ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    ".pdf",
    "application/pdf",
  ],
  ["Layouts"]: [
    ".pdf",
    "application/pdf",
    ".dwg",
    "application/acad",
    "image/vnd.dwg",
  ],
  ["PrivacyPolicy"]: [
    ".pdf",
    "application/pdf",
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ],
  ["HouseRules"]: [
    ".pdf",
    "application/pdf",
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ],
  ["CancellationPolicy"]: [
    ".pdf",
    "application/pdf",
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ],
  ["OtherPolicies"]: [
    ".pdf",
    "application/pdf",
    ".jpg",
    ".jpeg",
    ".png",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ],
};

const FORM_META_KEYS = new Set(["amenities", "documents"]);

export const formatFormData = (data: any) => {
  const propertyFields = Object.entries(data)
    .filter(([sectionKey]) => !FORM_META_KEYS.has(sectionKey))
    .map(([sectionKey, sectionValue]) => {
      if (sectionValue === null || typeof sectionValue !== "object") {
        return {
          fieldLevel: sectionKey,
          customFields: [] as { key: string; value?: string }[],
        };
      }
      return {
        fieldLevel: sectionKey,
        customFields: Object.entries(sectionValue as object).map(
          ([fieldKey, fieldValue]) => ({
            key: fieldKey,
            value: (() => {
              if (Array.isArray(fieldValue)) {
                return JSON.stringify(fieldValue);
              }
              if (
                fieldValue &&
                typeof fieldValue === "object" &&
                "value" in fieldValue
              ) {
                return (fieldValue as { value: string }).value;
              }
              if (fieldValue instanceof Date) {
                return fieldValue.toISOString();
              }
              if (typeof fieldValue === "boolean") {
                return fieldValue ? "true" : "false";
              }
              return fieldValue != null ? String(fieldValue) : undefined;
            })(),
          })
        ),
      };
    })
    .filter((section) => section.customFields.length > 0);
  return propertyFields;
};

export const mandationPerTrackType: Partial<
  Record<PROPERTY_TRACKS, MandateFields<Track>>
> = {
  [PROPERTY_TRACKS.FINISHING]: {
    property: {
      compound: true,
    },
  },
  [PROPERTY_TRACKS.RENTAL]: {
    property: {
      compound: true,
      propertyType: true,
    },
  },
  [PROPERTY_TRACKS.RESALE]: {
    property: {
      compound: true,
      propertyType: true,
    },
  },
  [PROPERTY_TRACKS.HOSPITALITY]: {
    property: {
      compound: true,
      propertyType: true,
    },
  },
};

export interface ResaleListingFormFields {
  propertyDetails?: Partial<PropertyFormFields>;
  propertyPriceDetails?: Partial<PropertyPriceFormFields>;
  resaleDocumentsDetails?: Partial<ResaleDocumentsFormFields>;
  listingDescriptionDetails?: Partial<ListingDescriptionFormFields>;
}

export interface ResaleListingSubmitFormFields {
  propertyDetails?: PropertyFormFields;
  propertyPriceDetails?: PropertyPriceFormFields;
  resaleDocumentsDetails?: ResaleDocumentsFormFields;
  listingDescriptionDetails?: ListingDescriptionFormFields;
  publish: boolean;
}
